import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import { getFooterStackData } from 'features/navigation/footer/actions';

import { darkTheme, brand } from 'theme';
import { MuiThemeProvider } from '@material-ui/core/styles';

import styled from 'styled-components';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import SVG from 'react-inlinesvg';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import EmailIcon from 'mdi-material-ui/Email';

import FooterSubmenu from './elements/FooterSubmenu';

const FooterSVG = styled(SVG)`
  svg {
    path,
    g,
    polygon {
      fill: ${props =>
        props.color === 'secondary' || props.color === 'default'
          ? 'white'
          : 'black'};
    }
  }
`;

const FooterLogo = styled(FooterSVG)`
  svg {
    width: 50px;
    height: 50px;
  }
`;

const FooterWrapper = styled.div`
  flex-shrink: 0;
  position: relative;
  z-index: 1;
`;

const FooterSection = styled.div`
  background-color: ${darkTheme.palette.background.default};
  padding: 50px 24px 30px 24px;
`;

const FooterContainer = styled.div`
  padding: 20px 0;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
`;

const SmallFooterText = styled(Typography)`
  color: rgba(255, 255, 255, 0.75);
  font-size: 11px;
`;

const SubMenuHeader = styled(SmallFooterText)`
  &:not(:empty) {
    padding-top: 20px;
  }
  @media (min-width: 600px) {
    padding-top: 0;
    min-height: 50px;
  }
`;

const FooterDivider = styled.div`
  width: 100%;
  height: 4px;
  background: linear-gradient(${brand.main.gradient.primary});
`;

const FooterLink = styled.a`
  color: #fff;
  text-decoration: none;
  font-size: 13px;
  font-weight: bold;

  &:hover {
    color: rgba(255, 255, 255, 0.75);
  }
`;

const SubFooterLink = styled(FooterLink)`
  font-weight: 500;
  color: rgba(255, 255, 255, 0.75);
  font-size: 11px;

  &:hover {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const SmallFooterLink = styled(FooterLink)`
  color: rgba(255, 255, 255, 0.5);
  padding-left: 10px;
  font-size: 11px;
  font-weight: normal;
`;

const SocialLink = styled(IconButton)`
  opacity: 0.5;
`;

const FooterTagline = styled(Typography)`
  font-size: 24px;
  padding: 30px 30px 30px 0;
`;

const MinimalFooterContainer = styled.div`
  padding: 0 24px;
  width: 100%;
  position: relative;
  z-index: 10;
  @media (min-width: 800px) {
    top: -39px;
    margin-bottom: -17px;
  }
`;

const MinimalFooterText = styled(SmallFooterText)`
  color: rgba(0, 0, 0, 0.75);
`;

const MinimalFooterLink = styled(SmallFooterLink)`
  color: rgba(0, 0, 0, 0.75);

  &:hover {
    color: rgba(0, 0, 0, 0.5);
  }
`;

export class Footer extends React.PureComponent {
  componentDidMount() {
    this.props.getFooterStackData();
  }

  render() {
    const { contentStack, minimal, hidden, loading } = this.props;

    return !hidden && !loading ? (
      <>
        {!minimal && (
          <MuiThemeProvider theme={darkTheme}>
            <FooterWrapper data-swiftype-index="false">
              <FooterDivider />
              <FooterSection>
                <FooterContainer>
                  <Grid container>
                    <FooterSubmenu>
                      <div>
                        <FooterLogo color="default">
                          <svg viewBox="0 0 278.1 465.5">
                            <defs />
                            <path d="M0 435.9l29.6 29.6L278.1 217l-29.4-29.9zm215.9-281.8l-29.6-29.7L0 310.6l29.7 29.7zM30.1 215.9L153.8 92.1 124 62.5.7 185.9zM91.4 30L61.6 0 .3 61.5 30 91.4z" />
                          </svg>
                        </FooterLogo>
                      </div>
                      <FooterTagline variant="h5">
                        {contentStack.sub_heading}
                      </FooterTagline>
                    </FooterSubmenu>
                    {contentStack.menu_footer.map(
                      (footer_submenu, submenu_idx) => {
                        return (
                          <FooterSubmenu key={submenu_idx.toString()}>
                            <SubMenuHeader variant="overline" gutterBottom>
                              {footer_submenu.title}
                            </SubMenuHeader>
                            {footer_submenu.links.map((link, link_idx) => {
                              return (
                                <Fragment key={link_idx.toString()}>
                                  <Typography noWrap variant="subtitle2">
                                    <FooterLink href={link.url}>
                                      {link.title}
                                    </FooterLink>
                                  </Typography>
                                  {link.sub_links.map(
                                    (sublink, sublink_idx) => {
                                      return (
                                        <Typography
                                          noWrap
                                          variant="subtitle2"
                                          key={sublink_idx.toString()}
                                        >
                                          <SubFooterLink href={sublink.url}>
                                            {sublink.title}
                                          </SubFooterLink>
                                        </Typography>
                                      );
                                    }
                                  )}
                                </Fragment>
                              );
                            })}
                          </FooterSubmenu>
                        );
                      }
                    )}
                  </Grid>
                </FooterContainer>
                <FooterContainer>
                  <Grid container alignItems="center" justify="space-between">
                    <Grid item>
                      <SmallFooterText variant="subtitle2">
                        Copyright &copy; {new Date().getFullYear()} Synthego
                        {contentStack.bottom_footer.legacy_menu.map(
                          (link, link_idx) => {
                            return (
                              <SmallFooterLink
                                href={link.url}
                                key={link_idx.toString()}
                              >
                                {link.title}
                              </SmallFooterLink>
                            );
                          }
                        )}
                      </SmallFooterText>
                    </Grid>
                    <Grid item>
                      {contentStack.bottom_footer.social_media.map(
                        (link, link_idx) => {
                          return (
                            <SocialLink
                              href={link.url}
                              key={link_idx.toString()}
                              title={link.title}
                            >
                              {link.logo.url && (
                                <FooterSVG
                                  color="secondary"
                                  src={link.logo.url}
                                />
                              )}
                            </SocialLink>
                          );
                        }
                      )}
                      {contentStack.subscribe_link && (
                        <Button
                          size="small"
                          color="primary"
                          variant="contained"
                          href={contentStack.subscribe_link.href}
                          target="_blank"
                        >
                          <EmailIcon />
                          &nbsp; {contentStack.subscribe_link.title}
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </FooterContainer>
              </FooterSection>
            </FooterWrapper>
          </MuiThemeProvider>
        )}
        {minimal && (
          <MinimalFooterContainer data-swiftype-index="false">
            <Grid container alignItems="center" spacing={40}>
              <Grid item>
                <MinimalFooterText variant="subtitle2">
                  <Link href="/" color="secondary">
                    Visit Synthego.com
                  </Link>
                </MinimalFooterText>
              </Grid>
              <Grid item>
                <MinimalFooterText variant="subtitle2">
                  Copyright &copy; {new Date().getFullYear()} Synthego
                  {contentStack.bottom_footer.legacy_menu.map(
                    (link, link_idx) => {
                      return (
                        <MinimalFooterLink
                          href={link.url}
                          key={link_idx.toString()}
                        >
                          {link.title}
                        </MinimalFooterLink>
                      );
                    }
                  )}
                </MinimalFooterText>
              </Grid>
            </Grid>
          </MinimalFooterContainer>
        )}
      </>
    ) : null;
  }
}

Footer.propTypes = {
  contentStack: PropTypes.shape({
    logo: PropTypes.shape({
      url: PropTypes.string.isRequired
    }).isRequired,
    sub_heading: PropTypes.string.isRequired,
    menu_footer: PropTypes.array,
    bottom_footer: PropTypes.shape({
      legacy_menu: PropTypes.array,
      social_media: PropTypes.array
    }).isRequired
  }).isRequired
};

export default connect(
  state => ({
    contentStack: state.footer.contentStack,
    loading: state.footer.loading,
    minimal: state.footer.minimal,
    hidden: state.footer.hidden
  }),
  { getFooterStackData }
)(Footer);
